import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useIsAdmin } from '../hooks/useIsAdmin';

interface AdminProtectedRouteProps {
  children: ReactNode;
}

/**
 * Protected route wrapper for admin pages.
 * Redirects non-admin users to the home page with a toast notification.
 */
export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const { isAdmin, isLoading } = useIsAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    // Only redirect after loading is complete and user is not an admin
    if (!isLoading && !isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/', { replace: true });
    }
  }, [isAdmin, isLoading, navigate]);

  // Show loading spinner while checking admin status
  if (isLoading) {
    return (
      <div 
        className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center"
        style={{ height: 'var(--app-height, 100dvh)' }}
      >
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-indigo-400 animate-spin" />
          <p className="text-slate-300">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Don't render children if not admin (redirect will happen via useEffect)
  if (!isAdmin) {
    return null;
  }

  return <>{children}</>;
}

